<template>
  <div
    class="autocomplete-wrapper"
    ref="wrapperRef"
    @keydown.down.prevent="move(1)"
    @keydown.up.prevent="move(-1)"
    @keydown.enter.prevent="selectHighlighted"
    tabindex="0"
  >
    <input
      :data-test="dataTest"
      v-model="searchTerm"
      :placeholder="placeholder"
      class="autocomplete-input"
      @focus="open = true"
      @input="open = true"
      @blur="onBlur"
      autocomplete="off"
    />
    <teleport to="body">
      <div v-if="open" :style="dropdownStyle" class="autocomplete-dropdown-portal">
        <template v-if="filteredOptions.length">
          <div
            v-for="(option, idx) in filteredOptions"
            :key="getOptionValue(option)"
            class="autocomplete-option"
            :class="{ highlighted: idx === highlighted }"
            @mousedown.prevent="selectOption(option)"
          >
            {{ getOptionLabel(option) }}
          </div>
        </template>
        <div v-else class="autocomplete-no-options">Sin opciones</div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
const wrapperRef = ref<HTMLElement | null>(null)
const dropdownStyle = ref('')

function updateDropdownPosition() {
  if (!open.value || !wrapperRef.value) return
  try {
    const rect = wrapperRef.value.getBoundingClientRect()
    const optionHeight = 48 // altura estimada de cada opción
    const maxHeight = 220
    const optionsCount = filteredOptions.value.length || 1
    const dropdownHeight = Math.min(optionsCount * optionHeight, maxHeight)
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    let style = `position: absolute; left: ${rect.left + window.scrollX}px; width: ${rect.width}px; z-index: 9999;`
    // Solo poner height si hay más de una opción, si no, dejar que el contenido defina el alto
    if (optionsCount * optionHeight < maxHeight) {
      style += `height: ${dropdownHeight}px;`
    } else {
      style += `max-height: ${maxHeight}px;`
    }
    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
      // Mostrar hacia abajo
      style += ` top: ${rect.bottom + window.scrollY}px; border-radius: 0 0 16px 16px;`
    } else {
      // Mostrar hacia arriba, pegado al input usando bottom
      style += ` bottom: ${window.innerHeight - rect.top - window.scrollY}px; border-radius: 16px 16px 0 0;`
      style = style.replace(/top:[^;]+;/, '') // Eliminar top si existe
    }
    dropdownStyle.value = style
  } catch {
    dropdownStyle.value = ''
  }
}

onMounted(() => {
  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateDropdownPosition, true)
  window.removeEventListener('resize', updateDropdownPosition)
})

interface Props {
  id: string
  modelValue: unknown
  options: unknown[]
  optionValue?: string | ((option: unknown) => unknown)
  optionLabel?: string | ((option: unknown) => string)
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  optionValue: 'value',
  optionLabel: 'label',
  placeholder: 'Buscar...',
})
const emit = defineEmits<{ 'update:modelValue': [value: unknown] }>()

const searchTerm = ref('')
const open = ref(false)
const highlighted = ref(-1)

watch(open, (val) => {
  if (val) nextTick(updateDropdownPosition)
})

const filteredOptions = computed(() => {
  if (!searchTerm.value) return props.options
  const term = searchTerm.value.toLowerCase()
  return props.options.filter((option) => {
    const label = getOptionLabel(option)
    return label && label.toString().toLowerCase().includes(term)
  })
})

function getOptionValue(option: unknown) {
  if (typeof props.optionValue === 'function') return props.optionValue(option)
  return (option as Record<string, unknown>)[props.optionValue as string]
}
function getOptionLabel(option: unknown) {
  if (typeof props.optionLabel === 'function') return props.optionLabel(option)
  return (option as Record<string, unknown>)[props.optionLabel as string]
}

function selectOption(option: unknown) {
  emit('update:modelValue', getOptionValue(option))
  searchTerm.value = getOptionLabel(option)
  open.value = false
}

function move(dir: number) {
  if (!open.value) open.value = true
  const len = filteredOptions.value.length
  if (!len) return
  highlighted.value = (highlighted.value + dir + len) % len
}

function selectHighlighted() {
  if (highlighted.value >= 0 && highlighted.value < filteredOptions.value.length) {
    selectOption(filteredOptions.value[highlighted.value])
  }
}

function onBlur() {
  // Cierra solo si el foco no va a una opción
  setTimeout(() => {
    open.value = false
  }, 100)
}

// Si cambia el modelValue desde fuera, actualiza el input y el estado del dropdown
watch(
  () => props.modelValue,
  (val, oldVal) => {
    const found = props.options.find((option) => getOptionValue(option) === val)
    if (found) {
      searchTerm.value = getOptionLabel(found)
      open.value = false
    } else {
      searchTerm.value = ''
      // Si se elimina la selección, abrir el dropdown y mostrar todos los resultados
      if (oldVal !== undefined && oldVal !== null) {
        open.value = true
      }
    }
  },
  { immediate: true },
)

const dataTest = computed(() => {
  return props.id ? `test-${props.id}` : undefined
})
</script>

<style scoped>
.autocomplete-wrapper {
  position: relative;
  width: 100%;
}
.autocomplete-input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
  background: #f2f2f2;
}
.autocomplete-dropdown-portal {
  position: absolute;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 16px 16px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  outline: none;
  appearance: none;
}
.autocomplete-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;
  color: #2b2a2a;
}
.autocomplete-option.highlighted,
.autocomplete-option:hover {
  background: #f2f2f2;
}
.autocomplete-no-options {
  padding: 12px 16px;
  color: #a1a1a1;
}
/* Separator line */
.autocomplete-wrapper::after {
  content: '';
  position: absolute;
  width: 2px;
  height: 36px;
  right: 40px;
  top: calc(50% - 36px / 2);
  background: #6b6b6b;
  pointer-events: none;
  z-index: 2;
}
/* Arrow */
.autocomplete-wrapper::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  right: 8px;
  top: calc(50% - 24px / 2);
  background: #6b6b6b;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='-6 1 35 24'%3E%3Cpath d='M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.14583 15.3708 4.8875 14.1125C3.62917 12.8542 3 11.3167 3 9.5C3 7.68333 3.62917 6.14583 4.8875 4.8875C6.14583 3.62917 7.68333 3 9.5 3C11.3167 3 12.8542 3.62917 14.1125 4.8875C15.3708 6.14583 16 7.68333 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8125 13.5625 12.6875 12.6875C13.5625 11.8125 14 10.75 14 9.5C14 8.25 13.5625 7.1875 12.6875 6.3125C11.8125 5.4375 10.75 5 9.5 5C8.25 5 7.1875 5.4375 6.3125 6.3125C5.4375 7.1875 5 8.25 5 9.5C5 10.75 5.4375 11.8125 6.3125 12.6875C7.1875 13.5625 8.25 14 9.5 14Z'/%3E%3C/svg%3E")
    no-repeat center;
  mask-size: 42px 42px;
  pointer-events: none;
  z-index: 3;
  transition: transform 0.3s ease;
}
</style>
